import type { W_Employee_FindById_VM, W_Employee_FindById_VM_Credential } from 'ab-api/web/employee/findById';
import type { W_Employee_Update_RM, W_Employee_Update_RM_Credential } from 'ab-api/web/employee/update';

import { filterMap } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { CDLStatus } from '@acceligentllc/shared/enums/cdlStatus';
import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { CountryCode } from '@acceligentllc/shared/enums/countryCode';
import type { SubscriptionStatus } from '@acceligentllc/shared/enums/subscriptionStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

export class CredentialFM {
	organizationCredentialId: number;
	/** DATE_ONLY */
	issuedDate: Nullable<string>;
	/** DATE_ONLY */
	expirationDate: Nullable<string>;
	name: string;

	static fromVM(vm: W_Employee_FindById_VM_Credential): CredentialFM {
		return {
			organizationCredentialId: vm.organizationCredentialId,
			issuedDate: vm.issuedDate ? TimeUtils.formatDate(vm.issuedDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY) : null,
			expirationDate: TimeUtils.formatDate(vm.expirationDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY),
			name: vm.name,
		};
	}

	static toRM(form: CredentialFM): W_Employee_Update_RM_Credential {
		if (!form.expirationDate) {
			throw new Error('Expiration date must be set');
		}

		return {
			organizationCredentialId: form.organizationCredentialId,
			issuedDate: form.issuedDate ? TimeUtils.formatDate(form.issuedDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY) : null,
			expirationDate: TimeUtils.formatDate(form.expirationDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
		};
	}

	static bulkToVM(vms: W_Employee_FindById_VM_Credential[]): CredentialFM[] {
		return vms.map(CredentialFM.fromVM);
	}

	static bulkToRM(vms: CredentialFM[]): W_Employee_Update_RM_Credential[] {
		return filterMap(
			vms,
			(_vm) => !!_vm.expirationDate,
			CredentialFM.toRM
		);
	}
}

interface UnionTypeFM {
	id: number;
	name: Nullable<string>;
}

interface WageRateFM {
	id: number;
	type: string;
	wageClassification: string;
}

interface SkillFM {
	id: number;
	name: string;
	color: ColorPalette;
}

export class EmployeeFM {
	id: number;
	accountId: number;
	fullName: string;
	email: Nullable<string>;
	phoneNumber: Nullable<string>;
	countryCode: Nullable<CountryCode>;
	wageRate: WageRateFM;
	skills: SkillFM[];
	unionType: Nullable<UnionTypeFM>;
	employeeStatusId: Nullable<number>;
	employeeStatusName: Nullable<string>;
	isStatusAvailable: Nullable<boolean>;
	showOnScheduleBoard: boolean;
	cdlStatus: CDLStatus;
	imageUrl: Nullable<string>;
	subscriptionStatus: SubscriptionStatus;
	locationNickname?: Nullable<string>;
	credentials: CredentialFM[];

	static fromVM(vm: W_Employee_FindById_VM): EmployeeFM {
		return {
			id: vm.id,
			accountId: vm.accountId,
			fullName: vm.fullName,
			email: vm.email,
			phoneNumber: vm.phoneNumber,
			countryCode: vm.countryCode,
			wageRate: vm.wageRate,
			skills: vm.skills,
			unionType: vm.unionType,
			employeeStatusId: vm.employeeStatusId,
			employeeStatusName: vm.employeeStatusName,
			isStatusAvailable: vm.isStatusAvailable ?? null,
			showOnScheduleBoard: vm.showOnScheduleBoard,
			cdlStatus: vm.cdlStatus,
			imageUrl: vm.imageUrl,
			subscriptionStatus: vm.subscriptionStatus,
			locationNickname: vm.locationNickname,
			credentials: CredentialFM.bulkToVM(vm.credentials),
		};
	}

	static toRM(form: EmployeeFM): W_Employee_Update_RM {
		return {
			id: form.id,
			accountId: form.accountId,
			fullName: form.fullName,
			email: form.email,
			phoneNumber: form.phoneNumber,
			countryCode: form.countryCode,
			wageRate: form.wageRate,
			skills: form.skills,
			unionType: form.unionType,
			employeeStatusId: form.employeeStatusId,
			employeeStatusName: form.employeeStatusName,
			isStatusAvailable: form.isStatusAvailable,
			showOnScheduleBoard: form.showOnScheduleBoard,
			cdlStatus: form.cdlStatus,
			imageUrl: form.imageUrl,
			subscriptionStatus: form.subscriptionStatus,
			locationNickname: form.locationNickname,
			credentials: CredentialFM.bulkToRM(form.credentials),
		};
	}
}

export type CredentialRHFRow = CredentialFM & { rhfId: string; };

export interface CredentialFormValues {
	credentials: CredentialRHFRow[];
}
